import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, scaleIn, displayFont } from './shared';

export interface DemoCard {
  id: string;
  screenshotId?: string;
  label: string;
  desc: string;
  url: string;
  names?: string;
  occasion?: string;
}

const DEFAULT_DEMOS: DemoCard[] = [
  { id: 'luxury',       label: 'Classical',    desc: 'Full-screen cinematic scroll with dark elegance',         url: 'https://wedsy.co/i/demo-luxury'        },
  { id: 'sage',         label: 'Sage',         desc: 'Delicate botanical motifs, soft greens and creams',       url: 'https://wedsy.co/i/demo-sage'          },
  { id: 'typographic',  label: 'Typographic',  desc: 'Editorial type-led design with refined elegance',         url: 'https://wedsy.co/i/demo-typographic'   },
  { id: 'polaroid',     label: 'Polaroid',     desc: 'Vintage photo frames with warm nostalgic charm',          url: 'https://wedsy.co/i/demo-polaroid'      },
  { id: 'sketch',       label: 'Illustration', desc: 'Hand-drawn ink illustrations with an artisan charm',      url: 'https://wedsy.co/i/demo-illustration'  },
  { id: 'floral',       label: 'Floral',       desc: 'Lush hand-painted florals with romantic warmth',          url: 'https://wedsy.co/i/demo-floral'        },
  { id: 'embossed',     label: 'Embossed',     desc: 'Raised letterpress card with tactile depth',              url: 'https://wedsy.co/i/demo-embossed'      },
  { id: 'geometric',    label: 'Geometric',    desc: 'Bold geometric patterns for modern minimalists',          url: 'https://wedsy.co/i/demo-geometric'     },
];

export function ThemeDemosSection({
  demos = DEFAULT_DEMOS,
  heading = <>See them <span className="text-primary">live</span></>,
  subheading = 'Every theme is a real invite — tap to explore it as your guests will.',
  badge = 'Theme Demos',
  ctaLabel = 'Create Your Invite',
}: {
  demos?: DemoCard[];
  heading?: React.ReactNode;
  subheading?: string;
  badge?: string;
  ctaLabel?: string;
}) {
  return (
    <section id="theme-demos" className="py-20 md:py-32 bg-background-card relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-12"
        >
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
            {badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4" style={displayFont}>
            {heading}
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base md:text-lg text-text-secondary max-w-xl mx-auto">
            {subheading}
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          className={`grid gap-4 sm:gap-5 max-w-5xl mx-auto ${demos.length <= 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-3xl' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}
        >
          {demos.map(t => (
            <motion.div key={t.id} variants={scaleIn} className="group">
              <a
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-2xl overflow-hidden shadow-md group-hover:shadow-xl transition-shadow duration-300 border border-border/40 relative mb-3"
              >
                <img
                  src={`/theme-previews/${t.screenshotId ?? t.id}.webp`}
                  alt={`${t.label} anniversary invitation`}
                  width={780}
                  height={1688}
                  className="w-full object-cover object-top h-[320px] sm:h-[400px]"
                  loading="lazy"
                />
                {t.names && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-4">
                    <p className="text-white text-sm font-semibold leading-tight">{t.names}</p>
                    {t.occasion && <p className="text-white/70 text-xs">{t.occasion}</p>}
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-end justify-center pb-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300">
                  <span className="bg-white text-gray-900 text-xs font-semibold px-4 py-2 rounded-full shadow-lg">
                    Open invite
                  </span>
                </div>
              </a>
              <p className="font-semibold text-text-primary text-sm mb-0.5 px-1">{t.label}</p>
              <p className="text-xs text-text-secondary leading-snug px-1">{t.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mt-12"
        >
          <Link to="/create" className="btn btn-primary hover:shadow-lg">
            {ctaLabel}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>

      </div>
    </section>
  );
}
